import {
  CampaignReview,
  ICampaign,
  CampaignAnalytics,
  CampaignPagination,
  CampaignContactCounts,
} from '../../types/campaigns/campaigns.types';
import {
  GET_CAMPAIGNS_REQUEST,
  GET_CAMPAIGNS_SUCCESS,
  GET_CAMPAIGNS_FAILURE,
  GET_CAMPAIGN_REQUEST,
  GET_CAMPAIGN_SUCCESS,
  GET_CAMPAIGN_FAILURE,
  CREATE_CAMPAIGN_REQUEST,
  CREATE_CAMPAIGN_SUCCESS,
  CREATE_CAMPAIGN_FAILURE,
  UPDATE_CAMPAIGN_REQUEST,
  UPDATE_CAMPAIGN_SUCCESS,
  UPDATE_CAMPAIGN_FAILURE,
  PUBLISH_CAMPAIGN_REQUEST,
  PUBLISH_CAMPAIGN_SUCCESS,
  PUBLISH_CAMPAIGN_FAILURE,
  CampaignActionTypes,
  CLEAR_CAMPAIGN_STATE,
  CLEAR_PUBLISH_CAMPAIGN_STATE,
  REVIEW_CAMPAIGN_REQUEST,
  REVIEW_CAMPAIGN_SUCCESS,
  REVIEW_CAMPAIGN_FAILURE,
  GET_CAMPAIGN_ANALYTICS_REQUEST,
  GET_CAMPAIGN_ANALYTICS_SUCCESS,
  GET_CAMPAIGN_ANALYTICS_FAILURE,
  DUPLICATE_CAMPAIGN_REQUEST,
  DUPLICATE_CAMPAIGN_SUCCESS,
  DUPLICATE_CAMPAIGN_FAILURE,
  DELETE_CAMPAIGN_REQUEST,
  DELETE_CAMPAIGN_SUCCESS,
  DELETE_CAMPAIGN_FAILURE,
  CLEAR_DELETE_STATE,
  PAUSE_CAMPAIGN_REQUEST,
  PAUSE_CAMPAIGN_SUCCESS,
  PAUSE_CAMPAIGN_FAILURE,
  GET_CAMPAIGN_CONTACT_COUNTS_REQUEST,
  GET_CAMPAIGN_CONTACT_COUNTS_SUCCESS,
  GET_CAMPAIGN_CONTACT_COUNTS_FAILURE,
  CLEAR_PAUSE_CAMPAIGN_STATE,
} from './campaigns.types';

export interface CampaignState {
  campaigns: ICampaign[];
  selectedCampaign: ICampaign | null;
  pagination: CampaignPagination;
  loading: boolean;
  error: string | null;
  success: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  publishLoading: boolean;
  reviewLoading: boolean;
  reviews: CampaignReview | null;
  analyticsLoading: boolean;
  analytics: CampaignAnalytics | null;
  duplicateLoading: boolean;
  deleteLoading?: boolean;
  deleteSuccess?: boolean;
  publishSuccess?: boolean;
  contactCountsLoading: boolean;
  contactCounts: CampaignContactCounts | null;
  contactCountsError: string | null;
  pauseCampaignSuccess: boolean;
  pauseCampaignError: boolean;
  pauseCampaignLoading: boolean;
}

const initialState: CampaignState = {
  campaigns: [],
  selectedCampaign: null,
  pagination: {
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
  success: false,
  createLoading: false,
  updateLoading: false,
  publishLoading: false,
  reviewLoading: false,
  reviews: null,
  analyticsLoading: false,
  analytics: null,
  duplicateLoading: false,
  contactCountsLoading: false,
  contactCounts: null,
  contactCountsError: null,
  deleteLoading: false,
  deleteSuccess: false,
  publishSuccess: false,
  pauseCampaignSuccess: false,
  pauseCampaignError: false,
  pauseCampaignLoading: false,
};

const campaignReducer = (
  state: CampaignState = initialState,
  action: CampaignActionTypes
): CampaignState => {
  switch (action.type) {
    case GET_CAMPAIGNS_REQUEST:
    case GET_CAMPAIGN_REQUEST:
      return {
        ...state,
        campaigns: [],
        selectedCampaign: null,
        loading: true,
        error: null,
      };

    case CREATE_CAMPAIGN_REQUEST:
      return {
        ...state,
        createLoading: true,
        error: null,
      };

    case UPDATE_CAMPAIGN_REQUEST:
      return {
        ...state,
        updateLoading: true,
        error: null,
      };

    case PUBLISH_CAMPAIGN_REQUEST:
      return {
        ...state,
        publishLoading: true,
        error: null,
      };

    case GET_CAMPAIGNS_SUCCESS:
      return {
        ...state,
        loading: false,
        campaigns: action.payload.campaigns,
        pagination: action.payload.pagination,
        error: null,
      };

    case GET_CAMPAIGN_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedCampaign: action.payload,
        error: null,
      };

    case CREATE_CAMPAIGN_SUCCESS:
      return {
        ...state,
        createLoading: false,
        campaigns: [...state.campaigns, action.payload],
        success: true,
        error: null,
      };

    case UPDATE_CAMPAIGN_SUCCESS:
      return {
        ...state,
        updateLoading: false,
        campaigns: state.campaigns.map((campaign) =>
          campaign._id === action.payload._id ? action.payload : campaign
        ),
        selectedCampaign:
          state.selectedCampaign?._id === action.payload._id
            ? action.payload
            : state.selectedCampaign,
        success: true,
        error: null,
      };

    case PUBLISH_CAMPAIGN_SUCCESS:
      return {
        ...state,
        publishLoading: false,
        campaigns: state.campaigns.map((campaign) =>
          campaign._id === action.payload._id ? action.payload : campaign
        ),
        selectedCampaign:
          state.selectedCampaign?._id === action.payload._id
            ? action.payload
            : state.selectedCampaign,
        publishSuccess: true,
        error: null,
      };

    case CLEAR_CAMPAIGN_STATE:
      return {
        ...state,
        success: false,
        error: null,
      };

    case GET_CAMPAIGNS_FAILURE:
    case GET_CAMPAIGN_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case CREATE_CAMPAIGN_FAILURE:
      return {
        ...state,
        createLoading: false,
        error: action.payload,
      };

    case UPDATE_CAMPAIGN_FAILURE:
      return {
        ...state,
        updateLoading: false,
        error: action.payload,
      };

    case PUBLISH_CAMPAIGN_FAILURE:
      return {
        ...state,
        publishLoading: false,
        error: action.payload,
      };

    case REVIEW_CAMPAIGN_REQUEST:
      return {
        ...state,
        reviewLoading: true,
        error: null,
        reviews: null,
      };

    case REVIEW_CAMPAIGN_SUCCESS:
      return {
        ...state,
        reviewLoading: false,
        reviews: action.payload,
        error: null,
      };

    case REVIEW_CAMPAIGN_FAILURE:
      return {
        ...state,
        reviewLoading: false,
        error: action.payload,
        reviews: null,
      };

    case GET_CAMPAIGN_ANALYTICS_REQUEST:
      return {
        ...state,
        analyticsLoading: true,
        error: null,
        analytics: null,
      };

    case GET_CAMPAIGN_ANALYTICS_SUCCESS:
      return {
        ...state,
        analyticsLoading: false,
        analytics: action.payload,
        error: null,
      };

    case GET_CAMPAIGN_ANALYTICS_FAILURE:
      return {
        ...state,
        analyticsLoading: false,
        error: action.payload,
        analytics: null,
      };

    case GET_CAMPAIGN_CONTACT_COUNTS_REQUEST:
      return {
        ...state,
        contactCountsLoading: true,
        contactCountsError: null,
      };

    case GET_CAMPAIGN_CONTACT_COUNTS_SUCCESS:
      return {
        ...state,
        contactCountsLoading: false,
        contactCounts: action.payload,
        contactCountsError: null,
      };

    case GET_CAMPAIGN_CONTACT_COUNTS_FAILURE:
      return {
        ...state,
        contactCountsLoading: false,
        contactCountsError: action.payload,
      };

    case DUPLICATE_CAMPAIGN_REQUEST:
      return {
        ...state,
        duplicateLoading: true,
        error: null,
      };

    case DUPLICATE_CAMPAIGN_SUCCESS:
      return {
        ...state,
        duplicateLoading: false,
        campaigns: [...state.campaigns, action.payload],
        success: true,
        error: null,
      };

    case DUPLICATE_CAMPAIGN_FAILURE:
      return {
        ...state,
        duplicateLoading: false,
        error: action.payload,
      };

    case DELETE_CAMPAIGN_REQUEST:
      return {
        ...state,
        deleteLoading: true,
        error: null,
      };

    case DELETE_CAMPAIGN_SUCCESS:
      return {
        ...state,
        deleteLoading: false,
        campaigns: state.campaigns.filter(
          (c) => c._id !== action.payload.campaignId
        ),
        deleteSuccess: true,
        error: null,
      };

    case DELETE_CAMPAIGN_FAILURE:
      return {
        ...state,
        deleteLoading: false,
        error: action.payload,
      };

    case CLEAR_DELETE_STATE:
      return {
        ...state,
        deleteSuccess: false,
        error: null,
      };

    case CLEAR_PUBLISH_CAMPAIGN_STATE:
      return {
        ...state,
        publishSuccess: false,
        error: null,
      };

    case PAUSE_CAMPAIGN_REQUEST:
      return {
        ...state,
        pauseCampaignLoading: true,
        error: null,
      };

    case PAUSE_CAMPAIGN_SUCCESS:
      return {
        ...state,
        pauseCampaignLoading: false,
        selectedCampaign: null,
        pauseCampaignSuccess: true,
        error: null,
      };

    case PAUSE_CAMPAIGN_FAILURE:
      return {
        ...state,
        pauseCampaignLoading: false,
        pauseCampaignError: true,
        error: action.payload,
      };

    case CLEAR_PAUSE_CAMPAIGN_STATE:
      return {
        ...state,
        pauseCampaignLoading: false,
        pauseCampaignError: false,
        pauseCampaignSuccess: false,
        error: null,
      };

    default:
      return state;
  }
};

export default campaignReducer;
